import { Button } from "@/components/ui/button";
import {
  Loader2Icon,
  ArrowLeftIcon,
  ArrowRightIcon,
  FlagIcon,
  CheckIcon,
} from "lucide-react";
import type { Question } from "@/types/api";
import type {
  UIAnswer,
  FeedbackState,
  CodeReviewContent,
  CodeReviewUIAnswer,
  RefactorContent,
  RefactorUIAnswer,
  HLDContent,
} from "@/types/assessment";
import {
  getQuestionContent,
  isCodeReviewHiddenAnswer,
  isRefactorHiddenAnswer,
  isHLDHiddenAnswer,
  isMultiSelectHiddenAnswer,
} from "@/types/assessment";
import { QuestionContent } from "./question-content";
import { FeedbackPanel } from "./feedback-panel";
import {
  SingleChoiceInput,
  MultiSelectInput,
  OpenAnswerInput,
  CodeReviewInput,
  RefactorInput,
  HLDWizardInput,
} from "./answer-input";
import { HLDInput } from "./answer-input/hld";
import { VideoPlayer } from "@/components/ui/video-player";
import type { HLDWizardContent, HLDWizardResponse } from "@/types/hld-wizard";

interface QuestionContainerProps {
  question: Question;
  answer: UIAnswer | null;
  onAnswerChange: (answer: UIAnswer) => void;
  onLanguageSave?: (language: string) => void;
  onVimToggle?: (enabled: boolean) => void;
  onSubmit: () => void;
  onNext: () => void;
  onPrevious?: () => void;
  onFinish?: () => void;
  isSubmitting: boolean;
  canSubmit: boolean;
  feedback: FeedbackState | null;
  showFeedback: boolean;
  isLastQuestion: boolean;
  isFirstQuestion?: boolean;
  isReviewMode?: boolean;
  hiddenAnswer?: Record<string, unknown>;
  videoExplanation?: string;
  userDefaultLanguage?: string;
  userVimEnabled?: boolean;
  // Blind-mode flag: when true, suppresses correctness UI (red/green coloring,
  // feedback panel, answer-explanation video) at the rendering layer. The
  // underlying disabled/locked state still follows showFeedback so revisited
  // answers stay visually selected (grey) but non-interactive.
  blindMode?: boolean;
}

export function QuestionContainer({
  question,
  answer,
  onAnswerChange,
  onLanguageSave,
  onVimToggle,
  onSubmit,
  onNext,
  onPrevious,
  onFinish,
  isSubmitting,
  canSubmit,
  feedback,
  showFeedback,
  isLastQuestion,
  isFirstQuestion = false,
  isReviewMode = false,
  hiddenAnswer,
  videoExplanation,
  userDefaultLanguage,
  userVimEnabled,
  blindMode = false,
}: QuestionContainerProps) {
  // Gate every "show results" surface on blind mode. In blind mode the
  // answer-input renders the user's pick with the same grey "selected"
  // styling it uses pre-submit — no red/green correctness coloring.
  // showFeedback still drives `disabled` (preserves the lock-after-submit
  // semantics) so revisited blind answers display selected without being
  // re-editable.
  const showResults = showFeedback && !blindMode;
  const content = getQuestionContent(question);

  // Derive correctOptions fallback: if correctOptions is missing but isCorrect is true,
  // the selected answer is the correct one
  const getCorrectOptionId = (): string | undefined => {
    if (feedback?.correctOptions?.[0]) {
      return feedback.correctOptions[0];
    }
    // Fallback: if answer is correct, the selected option is the correct option
    if (feedback?.isCorrect && answer) {
      return answer as string;
    }
    return undefined;
  };

  const getCorrectOptionIds = (): string[] => {
    if (feedback?.correctOptions && feedback.correctOptions.length > 0) {
      return feedback.correctOptions;
    }
    // Fallback: if answer is correct, the selected options are the correct options
    if (feedback?.isCorrect && answer && Array.isArray(answer)) {
      return answer;
    }
    return [];
  };

  const renderAnswerInput = () => {
    switch (question.type) {
      case "single_choice": {
        if (!("options" in content)) return null;
        return (
          <SingleChoiceInput
            options={content.options}
            value={(answer as string) ?? null}
            onChange={(value) => onAnswerChange(value)}
            disabled={showFeedback}
            showResults={showResults}
            correctOptionId={getCorrectOptionId()}
            selectedOptionId={(answer as string) ?? undefined}
          />
        );
      }
      case "multi_select": {
        if (!("options" in content)) return null;

        // Extract per-option feedback from hiddenAnswer
        const optionFeedback =
          hiddenAnswer && isMultiSelectHiddenAnswer(hiddenAnswer)
            ? Object.fromEntries(
                hiddenAnswer.options
                  .filter((opt) => opt.feedback)
                  .map((opt) => [String(opt.option_id), opt.feedback!])
              )
            : undefined;

        return (
          <MultiSelectInput
            options={content.options}
            value={(answer as string[]) ?? []}
            onChange={(value) => onAnswerChange(value)}
            disabled={showFeedback}
            showResults={showResults}
            correctOptionIds={getCorrectOptionIds()}
            selectedOptionIds={(answer as string[]) ?? []}
            optionFeedback={optionFeedback}
          />
        );
      }
      case "open_answer": {
        return (
          <OpenAnswerInput
            value={(answer as string) ?? ""}
            onChange={(value) => onAnswerChange(value)}
            disabled={showFeedback}
            maxLength={"max_length" in content ? content.max_length : undefined}
            context={"context" in content ? content.context : undefined}
          />
        );
      }
      case "code_review": {
        const codeReviewContent = content as CodeReviewContent;
        return (
          <CodeReviewInput
            content={codeReviewContent}
            value={answer as CodeReviewUIAnswer | null}
            onChange={(value) => onAnswerChange(value)}
            onLanguageSave={onLanguageSave}
            disabled={showFeedback}
            showResults={showResults}
            hiddenAnswer={
              hiddenAnswer && isCodeReviewHiddenAnswer(hiddenAnswer)
                ? hiddenAnswer
                : undefined
            }
            userDefaultLanguage={userDefaultLanguage}
          />
        );
      }
      case "refactor": {
        const refactorContent = content as RefactorContent;
        return (
          <RefactorInput
            content={refactorContent}
            value={answer as RefactorUIAnswer | null}
            onChange={(value) => onAnswerChange(value)}
            onLanguageSave={onLanguageSave}
            disabled={showFeedback}
            showResults={showResults}
            hiddenAnswer={
              hiddenAnswer && isRefactorHiddenAnswer(hiddenAnswer)
                ? hiddenAnswer
                : undefined
            }
            userDefaultLanguage={userDefaultLanguage}
            vimEnabled={userVimEnabled}
            onVimToggle={onVimToggle}
          />
        );
      }
      case "hld_wizard": {
        const wizardContent = content as unknown as HLDWizardContent;
        return (
          <HLDWizardInput
            content={wizardContent}
            value={(answer as HLDWizardResponse) ?? null}
            onChange={(value) => onAnswerChange(value)}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            disabled={showFeedback || isReviewMode}
          />
        );
      }
      case "hld": {
        const hldContent = content as HLDContent;
        return (
          <HLDInput
            content={hldContent}
            value={(answer as string) ?? null}
            onChange={(value) => onAnswerChange(value)}
            disabled={showFeedback}
            showResults={showResults}
            hiddenAnswer={
              hiddenAnswer && isHLDHiddenAnswer(hiddenAnswer)
                ? hiddenAnswer
                : undefined
            }
            vimEnabled={userVimEnabled}
            onVimToggle={onVimToggle}
          />
        );
      }
      default:
        return (
          <div className="p-4 bg-muted rounded-lg text-muted-foreground">
            This question type ({question.type}) is not supported yet.
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Question text */}
      {"text" in content && content.text && (
        <div className="space-y-4">
          <QuestionContent content={content.text} />
        </div>
      )}

      {/* Answer area */}
      <div className="space-y-4">
        {renderAnswerInput()}
      </div>

      {/* Video explanation (shown after submission, before feedback text).
          Suppressed in blind mode — answer-explanation videos are a
          correctness reveal that doesn't fit the diagnostic-only flow. */}
      {showFeedback && videoExplanation && !blindMode && (
        <VideoPlayer src={videoExplanation} title="Video Explanation" />
      )}

      {/* Feedback panel (shown after submission). Suppressed in blind mode
          so the user sees no per-question grading at all — they get the
          gap-analysis report at the end instead. */}
      {showFeedback && feedback && !blindMode && (
        <FeedbackPanel feedback={feedback} />
      )}

      {/* Navigation bar — hidden for hld_wizard while in progress (wizard provides its own nav) */}
      {!(question.type === "hld_wizard" && !showFeedback && !isReviewMode) && (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-4">
            {/* Previous button - hidden on first question */}
            {!isFirstQuestion ? (
              <Button variant="outline" onClick={onPrevious} size="lg" className="w-full sm:w-auto">
                <ArrowLeftIcon className="size-4" />
                Previous
              </Button>
            ) : (
              <div className="hidden sm:block" /> /* Spacer - only on desktop */
            )}

            {/* Right side: Submit and/or Next */}
            <div className="flex flex-col sm:flex-row gap-2">
              {/* Submit button (when not yet submitted and not in review mode) */}
              {!showFeedback && !isReviewMode && (
                <Button
                  onClick={onSubmit}
                  disabled={!canSubmit || isSubmitting}
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2Icon className="size-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Answer"
                  )}
                </Button>
              )}

              {/* Next/Finish button - always enabled for navigation */}
              {!isLastQuestion && (
                <Button variant="outline" onClick={onNext} size="lg" className="w-full sm:w-auto">
                  Next
                  <ArrowRightIcon className="size-4" />
                </Button>
              )}

              {/* Finish button - only on last question after answering */}
              {isLastQuestion && showFeedback && onFinish && (
                <Button onClick={onFinish} size="lg" className="w-full sm:w-auto">
                  {isReviewMode ? (
                    <>
                      <CheckIcon className="size-4" />
                      Done
                    </>
                  ) : (
                    <>
                      <FlagIcon className="size-4" />
                      Finish Assessment
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
